import React, { Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { useTranslation } from "react-i18next";
import PrivateRoute from "./components/PrivateRoute";
import { Toaster } from "react-hot-toast";

// ─── Auth pages (small, loaded eagerly for fast first paint) ───
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ForgotPassword from "./pages/auth/ForgotPassword";
// Where the LMS's links land: sign-in from the LMS (OpenID Connect).
import Launch from "./pages/auth/Launch";
import ResetPasswordOtp from "./pages/auth/ResetPasswordOtp";
import { SectionShell } from "./ui/SectionShell";
import RouteErrorBoundary from "./components/common/RouteErrorBoundary";
import { LightOnly, applyTheme, readTheme } from "./ui/theme";

// While a page's code loads (first visit to it), and while auth resolves: the page's own background in the
// stored theme with the indigo spinner — not a bare full-screen spinner on the legacy page colour. The
// exam-day screens are light only (LightOnly below), so their fallback is too — not the result, which is
// read in the shell and follows the theme (docs/32 S12).
const EXAM_DAY_PATH = /^\/(exam\/[^/]+\/(?!result\/?$)|m\/)/;
const RouteFallback = () => (
  <div
    className="ux-root min-h-screen flex items-center justify-center"
    data-theme={typeof window !== "undefined" && EXAM_DAY_PATH.test(window.location.pathname) ? "light" : readTheme()}
    role="status"
    aria-label="Loading"
    data-testid="route-fallback"
  >
    <div className="animate-spin rounded-full h-10 w-10 border-2 border-indigo-200 border-t-indigo-600" />
  </div>
);

// ─── All other pages: lazy-loaded (code-split into separate chunks) ───
// This reduces main.js from ~3.6MB to ~500KB, dramatically improving load time.
const TeacherDashboardV2 = React.lazy(() => import("./pages/TeacherDashboard/TeacherDashboardV2"));
const TeacherCoursesV2 = React.lazy(() => import("./pages/TeacherDashboard/TeacherCoursesV2"));
const TeacherExamsV2 = React.lazy(() => import("./pages/TeacherDashboard/TeacherExamsV2"));
const TeacherProfileV2 = React.lazy(() => import("./pages/TeacherDashboard/TeacherProfileV2"));
const TeacherAccountSettingsV2 = React.lazy(() => import("./pages/TeacherDashboard/TeacherAccountSettingsV2"));
const TeacherWrap = React.lazy(() => import("./pages/TeacherDashboard/TeacherWrap"));
// Exam-only build: the student's home is the exam home. Only pages the exam build serves are routed
// (services/__tests__/productScope.test.js holds every shipped request and in-app link to it).
const ExamHomeV2 = React.lazy(() => import("./pages/StudentDashboard/ExamHomeV2"));
const NotificationsV2 = React.lazy(() => import("./pages/Notifications/NotificationsV2"));
// Legacy sections wrapped in v2 SectionShell — keep their logic, get v2 chrome
const ExamsV2 = React.lazy(() => import("./pages/StudentDashboard/ExamsV2"));
const MyAnalyticsV2 = React.lazy(() => import("./pages/StudentDashboard/MyAnalyticsV2"));
const CodingProfileV2 = React.lazy(() => import("./pages/StudentDashboard/CodingProfileV2"));
const MyActivityV2 = React.lazy(() => import("./pages/StudentDashboard/MyActivityV2"));
const LessonV2 = React.lazy(() => import("./pages/StudentDashboard/LessonV2"));
const LessonBuilder = React.lazy(() => import("./pages/TeacherDashboard/LessonBuilder"));
// A course's interactive lessons: the student's list (exam home › Lessons) and
// the teacher's (course card › Lessons), in front of the reader and the builder.
const StudentLessons = React.lazy(() => import("./pages/StudentDashboard/StudentLessons"));
const TeacherCourseLessons = React.lazy(() => import("./pages/TeacherDashboard/TeacherCourseLessons"));

// ─── Exam pages ───
const ExamInterface = React.lazy(() => import("./pages/Exam/student/ExamInterface"));
const ExamLobby = React.lazy(() => import("./pages/Exam/student/ExamLobby"));
// 3.6 — Scan / Upload from the test list: worksheet pages from this device.
const WorksheetScan = React.lazy(() => import("./pages/Exam/student/WorksheetScan"));
// The result inside the shell, a fresh page per exam id (docs/32 B3, S12).
const ExamResultRoute = React.lazy(() => import("./pages/Exam/student/ExamResultRoute"));
const ExamRegistration = React.lazy(() => import("./pages/Exam/student/ExamRegistration"));
const TeacherExamList = React.lazy(() => import("./pages/Exam/teacher/ExamList"));
const CreateExam = React.lazy(() => import("./pages/Exam/teacher/CreateExam"));
const ExamDetail = React.lazy(() => import("./pages/Exam/teacher/ExamDetail"));
const ExamGrading = React.lazy(() => import("./pages/Exam/teacher/ExamGrading"));
const LiveProctoringDashboard = React.lazy(() => import("./pages/Exam/teacher/LiveProctoringDashboard"));
// Offline exam pipeline (EXAM_OFFLINE; restored 2026-07-08)
const BulkScanUploadPage = React.lazy(() => import("./pages/Exam/admin/BulkScanUploadPage"));
// Teacher evaluator surface (EXAM_OFFLINE) — assigned-booklet queue + marking viewer.
const MyAssignedBooklets = React.lazy(() => import("./pages/Exam/teacher/MyAssignedBooklets"));
const TeacherBookletMarking = React.lazy(() => import("./pages/Exam/teacher/TeacherBookletMarking"));
const ProctoringReport = React.lazy(() => import("./pages/Exam/teacher/ProctoringReport"));
const QuestionBankManager = React.lazy(() => import("./pages/Exam/teacher/QuestionBankManager"));
// A course's CA plan and grade publishing (teacher), and its gradebook (student) —
// the two in-scope tabs of the LMS course pages.
const TeacherCourseGrades = React.lazy(() => import("./pages/TeacherDashboard/TeacherCourseGrades"));
const CourseGrades = React.lazy(() => import("./pages/Exam/student/CourseGrades"));
// Groups (sections) of a course the teacher teaches (parity 2.3).
const TeacherGroups = React.lazy(() => import("./pages/Exam/teacher/TeacherGroups"));
const SemesterResults = React.lazy(() => import("./pages/Exam/student/SemesterResults"));
const MyCertificates = React.lazy(() => import("./pages/Exam/student/MyCertificates"));
const ApplyCertificate = React.lazy(() => import("./pages/Exam/student/ApplyCertificate"));
// Phase 4 — public mobile-upload bridge (no login; the QR token is the credential).
const MobileUpload = React.lazy(() => import("./pages/Exam/mobile/MobileUpload"));
const SecondCamera = React.lazy(() => import("./pages/Exam/mobile/SecondCamera"));
// ─── Account Settings pages ───
const ProfileV2 = React.lazy(() => import("./pages/StudentDashboard/ProfileV2"));
const AccountSettingsV2 = React.lazy(() => import("./pages/StudentDashboard/AccountSettingsV2"));

const App = () => {
  // html.dark from the one stored theme (ui/theme.js) before anything is drawn.
  useEffect(() => { applyTheme(readTheme()); }, []);
  return (
    <AuthProvider>
      {/* The LMS's course, meeting and section-utility providers went with the
          LMS pages that consumed them. */}
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Layout />
      </Router>
      <Toaster position="top-right" reverseOrder={false} />
    </AuthProvider>
  );
};
// Role-aware /profile router — picks the right V2 wrapper per role.
const RoleAwareProfile = () => {
  const { user } = useAuth();
  return user?.role === "teacher" ? <TeacherProfileV2 /> : <ProfileV2 />;
};

// Auth-aware catch-all: logged-in → dashboard, not logged-in → login
const AuthRedirect = () => {
  const { user } = useAuth();
  if (user) {
    const dashPath = user.role === "admin" ? "/admin"
      : user.role === "teacher" ? "/teacher/dashboard"
      : "/student/dashboard";
    return <Navigate to={dashPath} replace />;
  }
  return <Navigate to="/login" replace />;
};

const Layout = () => {
  const location = useLocation();
  const { user, loading } = useAuth();
  // The teacher sub-pages' banners (TeacherWrap) in the chosen language (docs/32 S13).
  const { t } = useTranslation();
  const hideNavbarRoutes = [
    "/login",
    "/register",
    "/forgot-password",
    "/reset-password",
  ];

  // If loading AND no cached user (first visit / after logout), show spinner
  // to prevent route evaluation before auth is resolved.
  if (loading && !user) {
    return <RouteFallback />;
  }

  return (
    <div className="min-h-screen vf-mesh-bg">
      {/* Conditionally render Navbar */}
      {/* {!hideNavbarRoutes.includes(location.pathname) && <Navbar />} */}

      <main className="mx-auto">
        <Suspense fallback={<RouteFallback />}>
        <RouteErrorBoundary routeKey={location.pathname}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/launch" element={<Launch />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPasswordOtp />} />
          {/* Phase 4: mobile-upload bridge — public; the QR JWT token is the credential. */}
          <Route path="/m/upload/:token" element={<LightOnly><MobileUpload /></LightOnly>} />
          {/* The QR carries eight characters; the phone exchanges them here. */}
          <Route path="/m/sc/:code" element={<LightOnly><SecondCamera /></LightOnly>} />
          {/* The installed second-camera app starts here, with no code yet (secondCameraPwa.js). */}
          <Route path="/m/sc" element={<LightOnly><SecondCamera /></LightOnly>} />
          <Route
            path="/profile"
            element={
              <PrivateRoute roles={["student", "teacher"]}>
                <RoleAwareProfile />
              </PrivateRoute>
            }
          />
          <Route
            path="/student/activity-log"
            element={
              <PrivateRoute roles={["student"]}>
                <MyActivityV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/teacher/activity-log"
            element={
              <PrivateRoute roles={["teacher"]}>
                <MyActivityV2 />
              </PrivateRoute>
            }
          />
          {/* Interactive lessons — student viewer */}
          <Route
            path="/student/lesson/:courseID/:lessonID"
            element={
              <PrivateRoute roles={["student"]}>
                <LessonV2 />
              </PrivateRoute>
            }
          />
          {/* Teacher preview of a lesson (as students see it) */}
          <Route
            path="/teacher/lesson/:courseID/:lessonID"
            element={
              <PrivateRoute roles={["teacher", "admin"]}>
                <LessonV2 />
              </PrivateRoute>
            }
          />
          {/* Interactive-lesson authoring (builder) */}
          <Route
            path="/teacher/lesson-builder/:courseID"
            element={
              <PrivateRoute roles={["teacher", "admin"]}>
                <LessonBuilder />
              </PrivateRoute>
            }
          />
          <Route
            path="/teacher/lesson-builder/:courseID/:lessonID"
            element={
              <PrivateRoute roles={["teacher", "admin"]}>
                <LessonBuilder />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <Navigate to="/" replace />
            }
          />
          <Route
            path="/teacher/dashboard"
            element={
              <PrivateRoute roles={["teacher"]}>
                <TeacherDashboardV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/teacher/courses"
            element={
              <PrivateRoute roles={["teacher"]}>
                <TeacherCoursesV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/teacher/exams-home"
            element={
              <PrivateRoute roles={["teacher"]}>
                <TeacherExamsV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/student/lessons"
            element={
              <PrivateRoute roles={["student"]}>
                <StudentLessons />
              </PrivateRoute>
            }
          />
          <Route
            path="/teacher/course/:courseID/lessons"
            element={
              <PrivateRoute roles={["teacher"]}>
                <TeacherWrap title={t("banner.teacher_lessons_title")} subtitle={t("banner.teacher_lessons_subtitle")} badge={t("banner.teacher_lessons_badge")} tone="sky">
                  <TeacherCourseLessons />
                </TeacherWrap>
              </PrivateRoute>
            }
          />
          {/* ─── Student v2 (primary) ─── */}
          <Route
            path="/student/dashboard"
            element={
              <PrivateRoute roles={["student"]}>
                <ExamHomeV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/student/notifications"
            element={
              <PrivateRoute roles={["student"]}>
                <NotificationsV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/teacher/notifications"
            element={
              <PrivateRoute roles={["teacher"]}>
                <NotificationsV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/student/exams"
            element={
              <PrivateRoute roles={["student"]}>
                <ExamsV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/student/analytics"
            element={
              <PrivateRoute roles={["student"]}>
                <MyAnalyticsV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/student/coding-profile"
            element={
              <PrivateRoute roles={["student"]}>
                <CodingProfileV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/teacher/profile/account"
            element={
              <PrivateRoute roles={["teacher"]}>
                <TeacherAccountSettingsV2 />
              </PrivateRoute>
            }
          />
          <Route
            path="/student/profile/account"
            element={
              <PrivateRoute roles={["student"]}>
                <AccountSettingsV2 />
              </PrivateRoute>
            }
          />



          {/* ─── Exam Registration (Student) ─── */}
          <Route
            path="/exam-registration"
            element={
              <PrivateRoute roles={["student"]}>
                <SectionShell><ExamRegistration /></SectionShell>
              </PrivateRoute>
            }
          />

          {/* ─── Semester Results (Student) ─── */}
          <Route
            path="/my-results"
            element={
              <PrivateRoute roles={["student"]}>
                <SectionShell><SemesterResults /></SectionShell>
              </PrivateRoute>
            }
          />

          <Route
            path="/student/course/:courseID/grades"
            element={
              <PrivateRoute roles={["student"]}>
                <SectionShell><CourseGrades /></SectionShell>
              </PrivateRoute>
            }
          />

          {/* ─── My Certificates (Student) ─── */}
          <Route
            path="/my-certificates"
            element={
              <PrivateRoute roles={["student"]}>
                <SectionShell><MyCertificates /></SectionShell>
              </PrivateRoute>
            }
          />

          {/* ─── Apply for Certificate (Student) ─── */}
          <Route
            path="/apply-certificate"
            element={
              <PrivateRoute roles={["student"]}>
                <SectionShell><ApplyCertificate /></SectionShell>
              </PrivateRoute>
            }
          />

          {/* ─── Exam Routes (Student) ─── */}
          <Route
            path="/exam/:examId/lobby"
            element={
              <PrivateRoute roles={["student"]}>
                <LightOnly><ExamLobby /></LightOnly>
              </PrivateRoute>
            }
          />
          <Route
            path="/exam/:examId/take"
            element={
              <PrivateRoute roles={["student"]}>
                <LightOnly><ExamInterface /></LightOnly>
              </PrivateRoute>
            }
          />
          <Route
            path="/exam/:examId/scan"
            element={
              <PrivateRoute roles={["student"]}>
                <LightOnly><WorksheetScan /></LightOnly>
              </PrivateRoute>
            }
          />
          <Route
            path="/exam/:examId/result"
            element={
              <PrivateRoute roles={["student"]}>
                <ExamResultRoute />
              </PrivateRoute>
            }
          />

          {/* ─── Exam Routes (Teacher) ─── */}
          <Route
            path="/teacher/exams/:courseId"
            element={
              <PrivateRoute roles={["teacher"]}>
                <TeacherWrap title={t("banner.teacher_course_exams_title")} subtitle={t("banner.teacher_course_exams_subtitle")} badge={t("banner.teacher_course_exams_badge")} tone="amber">
                  <TeacherExamList />
                </TeacherWrap>
              </PrivateRoute>
            }
          />
          <Route
            path="/teacher/exam/create/:courseId"
            element={
              <PrivateRoute roles={["teacher"]}>
                <TeacherWrap title={t("banner.teacher_create_exam_title")} subtitle={t("banner.teacher_create_exam_subtitle")} badge={t("banner.teacher_create_exam_badge")} tone="amber">
                  <CreateExam />
                </TeacherWrap>
              </PrivateRoute>
            }
          />
          <Route
            path="/teacher/exam/:examId"
            element={
              <PrivateRoute roles={["teacher"]}>
                <TeacherWrap title={t("banner.teacher_exam_detail_title")} subtitle={t("banner.teacher_exam_detail_subtitle")} badge={t("banner.teacher_exam_detail_badge")} tone="amber">
                  <ExamDetail />
                </TeacherWrap>
              </PrivateRoute>
            }
          />
          <Route
            path="/teacher/exam/:examId/grade"
            element={
              <PrivateRoute roles={["teacher"]}>
                <TeacherWrap title={t("banner.teacher_grade_exam_title")} subtitle={t("banner.teacher_grade_exam_subtitle")} badge={t("banner.teacher_grade_exam_badge")} tone="amber" noBanner>
                  <ExamGrading />
                </TeacherWrap>
              </PrivateRoute>
            }
          />
          <Route
            path="/teacher/exam/:examId/live"
            element={
              <PrivateRoute roles={["teacher"]}>
                <TeacherWrap title={t("banner.teacher_live_proctoring_title")} subtitle={t("banner.teacher_live_proctoring_subtitle")} badge={t("banner.teacher_live_proctoring_badge")} tone="rose" noBanner>
                  <LiveProctoringDashboard />
                </TeacherWrap>
              </PrivateRoute>
            }
          />
          <Route
            path="/teacher/exam/:examId/proctoring/:studentId"
            element={
              <PrivateRoute roles={["teacher"]}>
                <TeacherWrap title={t("banner.teacher_proctoring_report_title")} subtitle={t("banner.teacher_proctoring_report_subtitle")} badge={t("banner.teacher_proctoring_report_badge")} tone="rose">
                  <ProctoringReport />
                </TeacherWrap>
              </PrivateRoute>
            }
          />
          {/* Offline exam pipeline (EXAM_OFFLINE; restored 2026-07-08) — bulk
              answer-booklet scan upload; backend 404s while the flag is off. */}
          <Route
            path="/teacher/exam/:examId/bulk-scan"
            element={
              <PrivateRoute roles={["teacher"]}>
                <TeacherWrap title={t("banner.teacher_bulk_scan_title")} subtitle={t("banner.teacher_bulk_scan_subtitle")} badge={t("banner.teacher_bulk_scan_badge")} tone="amber" noBanner>
                  <BulkScanUploadPage />
                </TeacherWrap>
              </PrivateRoute>
            }
          />
          {/* Teacher evaluator surface (EXAM_OFFLINE; same ship-dark pattern
              as bulk-scan above — backend 404s while the flag is off). */}
          <Route
            path="/teacher/evaluations"
            element={
              <PrivateRoute roles={["teacher"]}>
                <TeacherWrap title={t("banner.teacher_my_evaluations_title")} subtitle={t("banner.teacher_my_evaluations_subtitle")} badge={t("banner.teacher_my_evaluations_badge")} tone="amber" noBanner>
                  <MyAssignedBooklets />
                </TeacherWrap>
              </PrivateRoute>
            }
          />
          <Route
            path="/teacher/evaluations/:id/mark"
            element={
              <PrivateRoute roles={["teacher"]}>
                <TeacherWrap title={t("banner.teacher_script_marking_title")} subtitle={t("banner.teacher_script_marking_subtitle")} badge={t("banner.teacher_script_marking_badge")} tone="amber" noBanner>
                  <TeacherBookletMarking />
                </TeacherWrap>
              </PrivateRoute>
            }
          />
          <Route
            path="/teacher/groups/:courseId"
            element={
              <PrivateRoute roles={["teacher"]}>
                <TeacherWrap title={t("banner.teacher_groups_title")} subtitle={t("banner.teacher_groups_subtitle")} badge={t("banner.teacher_groups_badge")} tone="amber">
                  <TeacherGroups />
                </TeacherWrap>
              </PrivateRoute>
            }
          />
          <Route
            path="/teacher/course/:courseID/grades"
            element={
              <PrivateRoute roles={["teacher"]}>
                <TeacherWrap title={t("banner.teacher_course_grades_title")} subtitle={t("banner.teacher_course_grades_subtitle")} badge={t("banner.teacher_course_grades_badge")} tone="violet">
                  <TeacherCourseGrades />
                </TeacherWrap>
              </PrivateRoute>
            }
          />
          <Route
            path="/teacher/question-bank/:courseId"
            element={
              <PrivateRoute roles={["teacher"]}>
                <TeacherWrap title={t("banner.teacher_question_bank_title")} subtitle={t("banner.teacher_question_bank_subtitle")} badge={t("banner.teacher_question_bank_badge")} tone="amber">
                  <QuestionBankManager />
                </TeacherWrap>
              </PrivateRoute>
            }
          />

          <Route path="/" element={<AuthRedirect />} />
          <Route path="*" element={<AuthRedirect />} />
        </Routes>
        </RouteErrorBoundary>
        </Suspense>
      </main>
    </div>
  );
};

export default App;
